import * as React from "react";
import {
  Box,
  Typography,
  TextField,
  Button,
  Paper,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  IconButton,
  Chip,
  Avatar,
  Alert,
  CircularProgress,
  Divider,
  Grid,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import AddIcon from "@mui/icons-material/Add";
import RemoveIcon from "@mui/icons-material/Remove";
import { useQuery, useLazyQuery, useMutation } from "@apollo/client";
import {
  SEARCH_USERS_BY_EMAIL,
  GET_ALL_BADGES,
} from "../../data/graphql/queries";
import {
  ASSIGN_BADGE_TO_USER,
  UNASSIGN_BADGE_FROM_USER,
} from "../../data/graphql/mutations";
import { UserType, BadgeType } from "../../data/types";

interface AdminManageUsersProps {}

export default function AdminManageUsers(props: AdminManageUsersProps) {
  const [searchEmail, setSearchEmail] = React.useState("");
  const [selectedUser, setSelectedUser] = React.useState<UserType | null>(null);
  const [successMessage, setSuccessMessage] = React.useState("");
  const [errorMessage, setErrorMessage] = React.useState("");

  // GraphQL queries and mutations
  const [
    searchUsers,
    { loading: searchLoading, error: searchError, data: searchData },
  ] = useLazyQuery(SEARCH_USERS_BY_EMAIL);

  const {
    loading: badgesLoading,
    error: badgesError,
    data: badgesData,
  } = useQuery(GET_ALL_BADGES);

  const [assignBadge, { loading: assignLoading }] = useMutation(
    ASSIGN_BADGE_TO_USER,
    {
      onCompleted: (data) => {
        setSelectedUser(data.assignBadgeToUser);
        setSuccessMessage("Badge assigned successfully!");
        setTimeout(() => setSuccessMessage(""), 3000);
      },
      onError: (error) => {
        setErrorMessage(`Error assigning badge: ${error.message}`);
        setTimeout(() => setErrorMessage(""), 5000);
      },
    }
  );

  const [unassignBadge, { loading: unassignLoading }] = useMutation(
    UNASSIGN_BADGE_FROM_USER,
    {
      onCompleted: (data) => {
        setSelectedUser(data.unassignBadgeFromUser);
        setSuccessMessage("Badge removed successfully!");
        setTimeout(() => setSuccessMessage(""), 3000);
      },
      onError: (error) => {
        setErrorMessage(`Error removing badge: ${error.message}`);
        setTimeout(() => setErrorMessage(""), 5000);
      },
    }
  );

  const handleSearch = () => {
    if (searchEmail.trim()) {
      searchUsers({ variables: { email: searchEmail.trim() } });
    }
  };

  const handleAssignBadge = (badgeId: string) => {
    if (selectedUser) {
      assignBadge({
        variables: {
          userId: selectedUser.id,
          badgeId: badgeId,
        },
      });
    }
  };

  const handleUnassignBadge = (badgeId: string) => {
    if (selectedUser) {
      unassignBadge({
        variables: {
          userId: selectedUser.id,
          badgeId: badgeId,
        },
      });
    }
  };

  const isUserHasBadge = (badgeId: string): boolean => {
    return selectedUser?.badges?.some((badge) => badge.id === badgeId) || false;
  };

  const availableBadges = badgesData?.getAllBadges || [];
  const searchResults = searchData?.searchUsersByEmail || [];

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h4" sx={{ mb: 3 }}>
        Manage Users
      </Typography>

      {/* Alerts */}
      {successMessage && (
        <Alert severity="success" sx={{ mb: 2 }}>
          {successMessage}
        </Alert>
      )}
      {errorMessage && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {errorMessage}
        </Alert>
      )}

      {/* Search Section */}
      <Paper sx={{ p: 3, mb: 3 }}>
        <Typography variant="h6" sx={{ mb: 2 }}>
          Search Users
        </Typography>
        <Box sx={{ display: "flex", gap: 2, alignItems: "center" }}>
          <TextField
            label="User Email"
            variant="outlined"
            value={searchEmail}
            onChange={(e) => setSearchEmail(e.target.value)}
            onKeyPress={(e) => e.key === "Enter" && handleSearch()}
            sx={{ flexGrow: 1 }}
            placeholder="Enter user email to search..."
          />
          <Button
            variant="contained"
            onClick={handleSearch}
            disabled={searchLoading || !searchEmail.trim()}
            startIcon={
              searchLoading ? <CircularProgress size={16} /> : <SearchIcon />
            }
          >
            Search
          </Button>
        </Box>

        {/* Search Results */}
        {searchError && (
          <Alert severity="error" sx={{ mt: 2 }}>
            Search error: {searchError.message}
          </Alert>
        )}

        {searchResults.length > 0 && (
          <Box sx={{ mt: 3 }}>
            <Typography variant="subtitle1" sx={{ mb: 1 }}>
              Search Results:
            </Typography>
            <List>
              {searchResults.map((user: UserType) => (
                <ListItem
                  key={user.id}
                  button
                  onClick={() => setSelectedUser(user)}
                  selected={selectedUser?.id === user.id}
                  sx={{
                    border: 1,
                    borderColor: "divider",
                    borderRadius: 1,
                    mb: 1,
                  }}
                >
                  <Avatar
                    src={user.profilePhotoUrl || undefined}
                    sx={{ mr: 2 }}
                  >
                    {user.profile?.nickname?.[0] || user.handle?.[0] || "?"}
                  </Avatar>
                  <ListItemText
                    primary={user.profile?.nickname || user.handle}
                    secondary={`${user.email} • @${user.handle} • ${user.role}`}
                  />
                  <ListItemSecondaryAction>
                    <Chip
                      label={user.verified ? "Verified" : "Unverified"}
                      color={user.verified ? "success" : "default"}
                      size="small"
                    />
                    {user.isPremiumUser && (
                      <Chip
                        label="Premium"
                        color="primary"
                        size="small"
                        sx={{ ml: 1 }}
                      />
                    )}
                  </ListItemSecondaryAction>
                </ListItem>
              ))}
            </List>
          </Box>
        )}
      </Paper>

      {/* Badge Management Section */}
      {selectedUser && (
        <Paper sx={{ p: 3 }}>
          <Typography variant="h6" sx={{ mb: 2 }}>
            Manage Badges for{" "}
            {selectedUser.profile?.nickname || selectedUser.handle}
          </Typography>

          <Grid container spacing={3}>
            {/* Current User Badges */}
            <Grid item xs={12} md={6}>
              <Typography variant="subtitle1" sx={{ mb: 2 }}>
                Current Badges ({selectedUser.badges?.length || 0})
              </Typography>
              {selectedUser.badges && selectedUser.badges.length > 0 ? (
                <List>
                  {selectedUser.badges.map((badge: BadgeType) => (
                    <ListItem
                      key={badge.id}
                      sx={{
                        border: 1,
                        borderColor: "divider",
                        borderRadius: 1,
                        mb: 1,
                      }}
                    >
                      <Avatar src={badge.imageUrl || undefined} sx={{ mr: 2 }}>
                        {badge.name[0]}
                      </Avatar>
                      <ListItemText
                        primary={badge.name}
                        secondary={`Assigned: ${new Date(
                          badge.createdAt
                        ).toLocaleDateString()}`}
                      />
                      <ListItemSecondaryAction>
                        <IconButton
                          color="error"
                          onClick={() => handleUnassignBadge(badge.id)}
                          disabled={unassignLoading}
                        >
                          <RemoveIcon />
                        </IconButton>
                      </ListItemSecondaryAction>
                    </ListItem>
                  ))}
                </List>
              ) : (
                <Typography color="text.secondary">
                  No badges assigned to this user.
                </Typography>
              )}
            </Grid>

            {/* Available Badges */}
            <Grid item xs={12} md={6}>
              <Typography variant="subtitle1" sx={{ mb: 2 }}>
                Available Badges
              </Typography>
              {badgesLoading ? (
                <CircularProgress />
              ) : badgesError ? (
                <Alert severity="error">
                  Error loading badges: {badgesError.message}
                </Alert>
              ) : (
                <List>
                  {availableBadges.map((badge: BadgeType) => {
                    const userHasBadge = isUserHasBadge(badge.id);
                    return (
                      <ListItem
                        key={badge.id}
                        sx={{
                          border: 1,
                          borderColor: userHasBadge
                            ? "success.main"
                            : "divider",
                          borderRadius: 1,
                          mb: 1,
                          backgroundColor: userHasBadge
                            ? "success.light"
                            : "transparent",
                        }}
                      >
                        <Avatar
                          src={badge.imageUrl || undefined}
                          sx={{ mr: 2 }}
                        >
                          {badge.name[0]}
                        </Avatar>
                        <ListItemText
                          primary={badge.name}
                          secondary={
                            userHasBadge
                              ? "Already assigned"
                              : "Available to assign"
                          }
                        />
                        <ListItemSecondaryAction>
                          {!userHasBadge && (
                            <IconButton
                              color="primary"
                              onClick={() => handleAssignBadge(badge.id)}
                              disabled={assignLoading}
                            >
                              <AddIcon />
                            </IconButton>
                          )}
                        </ListItemSecondaryAction>
                      </ListItem>
                    );
                  })}
                </List>
              )}
            </Grid>
          </Grid>
        </Paper>
      )}

      {/* Loading States */}
      {(assignLoading || unassignLoading) && (
        <Box sx={{ display: "flex", justifyContent: "center", mt: 2 }}>
          <CircularProgress />
        </Box>
      )}
    </Box>
  );
}
